import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
}

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    pointerEvents: 'none',
  },
  overlayOpen: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateY(-20px) scale(0.95)',
    transition:
      'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    opacity: 0,
  },
  modalOpen: {
    transform: 'translateY(0) scale(1)',
    opacity: 1,
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #ebebeb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '16px',
      height: '16px',
    },
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
  },
  footer: {
    padding: '20px',
    borderTop: '1px solid #ebebeb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
});

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  closeOnOverlayClick = true,
}) => {
  const classes = useStyles();
  const modalRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to allow render before animation starts
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
      document.body.style.overflow = 'hidden';
    } else {
      setIsAnimating(false);
      // Wait for animation to finish before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Clean up overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`${classes.overlay} ${isAnimating ? classes.overlayOpen : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        className={`${classes.modal} ${isAnimating ? classes.modalOpen : ''}`}
        ref={modalRef}
      >
        <div className={classes.header}>
          <h3 className={classes.title}>{title}</h3>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={classes.content}>{children}</div>
        {footer && <div className={classes.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
